/**
 * React Query Hooks for Groups Management
 *
 * Key features:
 * - Data comes pre-sorted with computed fields from server
 * - No derived data computation on frontend
 * - Reorder uses optimistic updates + legacy endpoint for reliability
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useNetworkID } from "Flocknote/stateHooks";

import { deleteGroup, fetchGroups, reorderGroupsLegacy } from "./fetch";
import groupsKeys from "./keys";

/**
 * Fetch groups with computed fields, pre-sorted in display order.
 *
 * The response is ready to render directly - no sorting or
 * derived data computation needed.
 *
 * @returns {UseQueryResult} React Query result with groups data
 */
export const useGroups = () => {
  return useQuery({
    queryKey: groupsKeys.list(),
    queryFn: fetchGroups,
    staleTime: 5 * 60 * 1000, // 5 minutes - use cached data

    // PERF: Disable refetches that can fire mid-interaction (e.g. during drag).
    // refetchOnMount is left at default (true) so invalidated cache data
    // (e.g. from localStorage hydration) triggers a background refetch.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Reorder a group with optimistic updates.
 *
 * V3 Architecture:
 * - Caller computes reorderedGroups array ONCE and passes it to mutation
 * - onMutate uses reorderedGroups for optimistic cache update (simple assignment)
 * - mutationFn uses reorderedGroups for API call (NO cache reads!)
 * - onSuccess invalidates cache for guaranteed consistency
 *
 * This eliminates race conditions where mutationFn might read stale/modified cache.
 *
 * NOTE: This hook only handles sibling reorders (same parent).
 * Re-parenting operations (Detach, Make Subgroup) use useReorderGroupsLegacy directly
 * with buildDetachFormData/buildReparentFormData helpers.
 *
 * @returns {UseMutationResult} React Query mutation
 */
export const useReorderGroup = () => {
  const queryClient = useQueryClient();
  const [networkID] = useNetworkID();

  return useMutation({
    // Variables shape: { reorderedGroups: Array, draggedId: number }
    mutationFn: async ({ reorderedGroups, draggedId }) => {
      // Guard: Don't make API call with empty data
      if (!reorderedGroups || reorderedGroups.length === 0) {
        console.warn("useReorderGroup: No groups provided, skipping API call");
        return { success: true, noOp: true };
      }

      // Build URLSearchParams from the pre-computed reordered array
      // NO CACHE READ - uses data passed in from mutation call
      const params = new URLSearchParams();

      reorderedGroups.forEach((group, index) => {
        params.append(`groups[${index}][groupID]`, group.ID);
        params.append(`groups[${index}][level]`, group.hierarchyLevel);
        params.append(`groups[${index}][parentID]`, group.parentGroupID);
      });

      params.append("groupIDThatChangedPos", draggedId);
      params.append("preservePreviousParentSubscriptions", "false");
      params.append("followingGroupDeletion", "false");

      // Call legacy endpoint (renumbers all groups sequentially)
      return reorderGroupsLegacy(networkID, params);
    },

    onMutate: async ({ reorderedGroups }) => {
      // Cancel in-flight queries to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: groupsKeys.list() });

      // Snapshot for rollback
      const previousData = queryClient.getQueryData(groupsKeys.list());

      // Optimistic update - use pre-computed array directly
      // NO COMPUTATION NEEDED - array comes from mutation variables
      queryClient.setQueryData(groupsKeys.list(), (old) => {
        if (!old) return old;
        return { ...old, groups: reorderedGroups };
      });

      return { previousData };
    },

    onError: (err, variables, context) => {
      // Rollback to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(groupsKeys.list(), context.previousData);
      }
    },

    onSuccess: () => {
      // SAFETY NET: Always invalidate after success to guarantee
      // cache matches server state, even if something unexpected happens
      queryClient.invalidateQueries({ queryKey: groupsKeys.list() });
    },
  });
};

/**
 * Delete a group.
 *
 * @returns {UseMutationResult} React Query mutation
 */
export const useDeleteGroup = () => {
  return useMutation({
    mutationFn: ({ groupID, deleteFromParent }) =>
      deleteGroup(groupID, deleteFromParent),
  });
};

/**
 * Legacy full-list reorder mutation (V1 endpoint).
 *
 * Used by re-parenting operations that need to update hierarchy:
 * - Hierarchy promotion (CreateSubgroup)
 * - Detach from parent (DetachFromParentGroup)
 * - Make subgroup (MakeSubgroupModal)
 *
 * These use buildDetachFormData/buildReparentFormData helpers to build FormData.
 *
 * @returns {UseMutationResult} React Query mutation
 */
export const useReorderGroupsLegacy = () => {
  const [networkID] = useNetworkID();

  return useMutation({
    mutationFn: (formData) => reorderGroupsLegacy(networkID, formData),
  });
};

/**
 * Get the query key for groups management list.
 * Useful for manual cache operations.
 */
export const getGroupsQueryKey = () => groupsKeys.list();
